import { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { 
  FiDollarSign, 
  FiCalendar, 
  FiCheckCircle, 
  FiChevronLeft, 
  FiChevronRight,
  FiClock,
  FiEdit2,
  FiFileText,
  FiPercent
} from 'react-icons/fi';
import { Bar } from 'react-chartjs-2';
import SignaturePad from 'react-signature-canvas';
import { format, addDays, addMonths, isBefore } from 'date-fns';
import { toast } from 'react-toastify';

const PaymentPlanWizard = ({ studentId: propStudentId, programId: propProgramId, onComplete, onCancel }) => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  
  // Get studentId and programId from props or URL params
  const studentId = propStudentId || searchParams.get('studentId') || '';
  const programId = propProgramId || searchParams.get('programId') || '';
  
  const [step, setStep] = useState(1);
  const [plan, setPlan] = useState({
    studentId,
    programId,
    name: '',
    totalAmount: 0,
    depositRequired: true,
    depositAmount: 0,
    installmentCount: 1,
    frequency: 'monthly',
    startDate: new Date(),
    lateFeeEnabled: true,
    lateFeeSettings: {
      type: 'percentage',
      value: 5,
      gracePeriod: 5
    },
    notes: '',
    signature: null
  });
  const [installments, setInstallments] = useState([]);
  const [signaturePad, setSignaturePad] = useState(null);

  // Update plan when studentId or programId changes (from URL params)
  useEffect(() => {
    if (studentId || programId) {
      setPlan(prev => ({
        ...prev,
        studentId: studentId || prev.studentId,
        programId: programId || prev.programId
      }));
    }
  }, [studentId, programId]);

  // Calculate installments when plan details change
  useEffect(() => {
    if (step >= 2) {
      calculateInstallments();
    }
  }, [
    plan.totalAmount, 
    plan.depositRequired, 
    plan.depositAmount, 
    plan.installmentCount, 
    plan.frequency, 
    plan.startDate
  ]);

  const calculateInstallments = () => {
    let calculatedAmount = plan.totalAmount || 0;
    let installments = [];
    const depositAmount = plan.depositRequired ? (plan.depositAmount || 0) : 0;
    
    // Subtract deposit from total
    calculatedAmount = Math.max(0, calculatedAmount - depositAmount);
    
    // Calculate each installment amount
    const installmentCount = Math.max(1, plan.installmentCount || 1);
    const installmentAmount = calculatedAmount / installmentCount;
    
    // Generate installment dates and amounts
    let currentDate = new Date(plan.startDate);
    
    if (plan.depositRequired && depositAmount > 0) {
      installments.push({
        amount: depositAmount,
        dueDate: currentDate,
        type: 'deposit'
      });
    }
    
    for (let i = 0; i < installmentCount; i++) {
      if (i > 0) {
        if (plan.frequency === 'weekly') {
          currentDate = addDays(currentDate, 7);
        } else if (plan.frequency === 'biweekly') {
          currentDate = addDays(currentDate, 14);
        } else {
          currentDate = addMonths(currentDate, 1);
        }
      }
      
      installments.push({
        amount: installmentAmount,
        dueDate: currentDate,
        type: 'installment'
      });
    }
    
    setInstallments(installments);
  };

  // Handle completion - either call onComplete callback or navigate back
  const handleComplete = (planData) => {
    if (onComplete) {
      onComplete(planData);
    } else {
      // If used as standalone route, show success and navigate back to fees page
      toast.success('Payment plan created successfully!');
      navigate('/trainer/fees');
    }
  };

  // Handle cancellation - either call onCancel callback or navigate back
  const handleCancel = () => {
    if (onCancel) {
      onCancel();
    } else {
      // If used as standalone route, navigate back to fees page
      navigate('/trainer/fees');
    }
  };

  const handleNext = () => {
    if (step === 4) {
      const planData = {
        ...plan,
        installments,
        signature: signaturePad?.getTrimmedCanvas().toDataURL('image/png') || null
      };
      handleComplete(planData);
    } else {
      setStep(step + 1);
    }
  };

  const handlePrev = () => {
    setStep(step - 1);
  };

  const handlePlanChange = (field, value) => {
    setPlan(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleLateFeeChange = (field, value) => {
    setPlan(prev => ({
      ...prev,
      lateFeeSettings: {
        ...prev.lateFeeSettings,
        [field]: value
      }
    }));
  };

  const clearSignature = () => {
    signaturePad?.clear();
  };

  const safeParseFloat = (value) => {
    const parsed = parseFloat(value);
    return isNaN(parsed) ? 0 : parsed;
  };

  const safeParseInt = (value) => {
    const parsed = parseInt(value);
    return isNaN(parsed) ? 1 : parsed;
  };

  const renderStep = () => {
    switch (step) {
      case 1:
        return (
          <div className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Plan Name</label>
              <input
                type="text"
                value={plan.name}
                onChange={(e) => handlePlanChange('name', e.target.value)}
                className="w-full border border-gray-300 rounded-md px-3 py-2"
                placeholder="e.g., Standard Payment Plan"
                required
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Total Amount ($)</label>
              <input
                type="number"
                value={plan.totalAmount || ''}
                onChange={(e) => handlePlanChange('totalAmount', safeParseFloat(e.target.value))}
                className="w-full border border-gray-300 rounded-md px-3 py-2"
                min="0"
                step="0.01"
                required
              />
            </div>
            
            <div className="flex items-center">
              <input
                type="checkbox"
                id="depositRequired"
                checked={plan.depositRequired}
                onChange={(e) => handlePlanChange('depositRequired', e.target.checked)}
                className="h-4 w-4 text-teal-600 focus:ring-teal-500 border-gray-300 rounded"
              />
              <label htmlFor="depositRequired" className="ml-2 block text-sm text-gray-700">
                Require Deposit
              </label>
            </div>
            
            {plan.depositRequired && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Deposit Amount ($)</label>
                <input
                  type="number"
                  value={plan.depositAmount || ''}
                  onChange={(e) => handlePlanChange('depositAmount', safeParseFloat(e.target.value))}
                  className="w-full border border-gray-300 rounded-md px-3 py-2"
                  min="0"
                  step="0.01"
                  max={plan.totalAmount || ''}
                />
              </div>
            )}
          </div>
        );
      
      case 2:
        return (
          <div className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Number of Installments</label>
              <input
                type="number"
                value={plan.installmentCount}
                onChange={(e) => handlePlanChange('installmentCount', safeParseInt(e.target.value))}
                className="w-full border border-gray-300 rounded-md px-3 py-2"
                min="1"
                max="36"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Payment Frequency</label>
              <select
                value={plan.frequency}
                onChange={(e) => handlePlanChange('frequency', e.target.value)}
                className="w-full border border-gray-300 rounded-md px-3 py-2"
              >
                <option value="weekly">Weekly</option>
                <option value="biweekly">Bi-weekly</option>
                <option value="monthly">Monthly</option>
              </select>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Start Date</label>
              <input
                type="date"
                value={format(plan.startDate, 'yyyy-MM-dd')}
                onChange={(e) => handlePlanChange('startDate', new Date(e.target.value))}
                className="w-full border border-gray-300 rounded-md px-3 py-2"
                min={format(new Date(), 'yyyy-MM-dd')}
              />
            </div>
            
            <div className="flex items-center">
              <input
                type="checkbox"
                id="lateFeeEnabled"
                checked={plan.lateFeeEnabled}
                onChange={(e) => handlePlanChange('lateFeeEnabled', e.target.checked)}
                className="h-4 w-4 text-teal-600 focus:ring-teal-500 border-gray-300 rounded"
              />
              <label htmlFor="lateFeeEnabled" className="ml-2 block text-sm text-gray-700">
                Apply Late Fees
              </label>
            </div>
            
            {plan.lateFeeEnabled && (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Type</label>
                  <select
                    value={plan.lateFeeSettings.type}
                    onChange={(e) => handleLateFeeChange('type', e.target.value)}
                    className="w-full border border-gray-300 rounded-md px-3 py-2"
                  >
                    <option value="percentage">Percentage</option>
                    <option value="fixed">Fixed Amount</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {plan.lateFeeSettings.type === 'percentage' ? 'Percentage' : 'Amount'}
                  </label>
                  <input
                    type="number"
                    value={plan.lateFeeSettings.value}
                    onChange={(e) => handleLateFeeChange('value', safeParseFloat(e.target.value))}
                    className="w-full border border-gray-300 rounded-md px-3 py-2"
                    min="0"
                    step={plan.lateFeeSettings.type === 'percentage' ? "0.1" : "1"}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Grace Period (days)</label>
                  <input
                    type="number"
                    value={plan.lateFeeSettings.gracePeriod}
                    onChange={(e) => handleLateFeeChange('gracePeriod', safeParseInt(e.target.value))}
                    className="w-full border border-gray-300 rounded-md px-3 py-2"
                    min="0"
                  />
                </div>
              </div>
            )}
          </div>
        );
      
      case 3:
        const installmentAmount = plan.installmentCount > 0 
          ? (plan.totalAmount - (plan.depositRequired ? plan.depositAmount : 0)) / plan.installmentCount
          : 0;

        return (
          <div className="space-y-6">
            <h3 className="text-lg font-medium text-gray-900">Payment Schedule Preview</h3>
            
            <div className="bg-gray-50 p-4 rounded-lg">
              <div className="flex justify-between items-center mb-2">
                <span className="font-medium">Total Amount:</span>
                <span className="font-bold">${(plan.totalAmount || 0).toFixed(2)}</span>
              </div>
              {plan.depositRequired && (
                <div className="flex justify-between items-center mb-2">
                  <span className="font-medium">Deposit:</span>
                  <span className="font-bold">${(plan.depositAmount || 0).toFixed(2)}</span>
                </div>
              )}
              <div className="flex justify-between items-center mb-2">
                <span className="font-medium">Installments:</span>
                <span className="font-bold">{plan.installmentCount} × ${installmentAmount.toFixed(2)}</span>
              </div>
            </div>
            
            <div className="h-64">
              {installments.length > 0 ? (
                <Bar
                  data={{
                    labels: installments.map((_, i) => `Payment ${i + 1}`),
                    datasets: [{
                      label: 'Payment Amount',
                      data: installments.map(i => i.amount),
                      backgroundColor: installments.map(i => 
                        i.type === 'deposit' ? '#10B981' : '#3B82F6'
                      ),
                      borderColor: installments.map(i => 
                        i.type === 'deposit' ? '#059669' : '#2563EB'
                      ),
                      borderWidth: 1
                    }]
                  }}
                  options={{
                    responsive: true,
                    maintainAspectRatio: false,
                    scales: {
                      y: {
                        beginAtZero: true,
                        ticks: {
                          callback: function(value) {
                            return '$' + value;
                          }
                        }
                      }
                    }
                  }}
                />
              ) : (
                <div className="h-full flex items-center justify-center text-gray-500">
                  No payment data to display
                </div>
              )}
            </div>
            
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">#</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Type</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Amount</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Due Date</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {installments.map((installment, index) => (
                    <tr key={index}>
                      <td className="px-4 py-3 whitespace-nowrap text-sm">{index + 1}</td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm capitalize">{installment.type}</td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm font-medium">
                        ${(installment.amount || 0).toFixed(2)}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm">
                        {format(installment.dueDate, 'MMM dd, yyyy')}
                        {isBefore(installment.dueDate, new Date()) && (
                          <span className="ml-2 text-xs text-rose-600">(Past Due)</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Additional Notes</label>
              <textarea
                value={plan.notes}
                onChange={(e) => handlePlanChange('notes', e.target.value)}
                className="w-full border border-gray-300 rounded-md px-3 py-2"
                rows="3"
                placeholder="Any special instructions or terms..."
              />
            </div>
          </div>
        );
      
      case 4:
        return (
          <div className="space-y-6">
            <h3 className="text-lg font-medium text-gray-900">Agreement & Signature</h3>
            
            <div className="bg-gray-50 p-4 rounded-lg text-sm">
              <p className="mb-3">By signing below, you agree to the following terms:</p>
              <ul className="list-disc pl-5 space-y-1">
                <li>All payments are due on the specified due dates</li>
                {plan.lateFeeEnabled && (
                  <li>
                    Late payments will incur a {plan.lateFeeSettings.type === 'percentage' ? 
                    `${plan.lateFeeSettings.value}%` : `$${plan.lateFeeSettings.value}`} fee after {plan.lateFeeSettings.gracePeriod} day grace period
                  </li>
                )}
                <li>Failure to make payments may result in suspension of services</li>
                <li>All payments are non-refundable unless otherwise stated</li>
              </ul>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Signature</label>
              <div className="border border-gray-300 rounded-md p-2">
                <SignaturePad 
                  canvasProps={{ 
                    width: '100%', 
                    height: 200,
                    className: 'signature-canvas bg-white'
                  }}
                  ref={(ref) => setSignaturePad(ref)}
                />
                <div className="mt-2 flex justify-end">
                  <button
                    onClick={clearSignature}
                    className="text-sm text-rose-600 hover:text-rose-800"
                  >
                    Clear Signature
                  </button>
                </div>
              </div>
            </div>
          </div>
        );
      
      default:
        return null;
    }
  };

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 max-w-3xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-semibold text-gray-800">
          {step === 1 && 'Plan Details'}
          {step === 2 && 'Payment Schedule'}
          {step === 3 && 'Plan Preview'}
          {step === 4 && 'Agreement'}
        </h2>
        <div className="flex items-center gap-1 text-sm text-gray-500">
          <span>Step {step} of 4</span>
        </div>
      </div>
      
      <div className="mb-6">
        <div className="flex items-center">
          <div className={`flex items-center justify-center w-8 h-8 rounded-full ${step >= 1 ? 'bg-teal-100 text-teal-800' : 'bg-gray-100 text-gray-500'}`}>
            {step > 1 ? <FiCheckCircle size={16} /> : '1'}
          </div>
          <div className={`flex-1 h-1 mx-2 ${step >= 2 ? 'bg-teal-500' : 'bg-gray-200'}`}></div>
          <div className={`flex items-center justify-center w-8 h-8 rounded-full ${step >= 2 ? 'bg-teal-100 text-teal-800' : 'bg-gray-100 text-gray-500'}`}>
            {step > 2 ? <FiCheckCircle size={16} /> : '2'}
          </div>
          <div className={`flex-1 h-1 mx-2 ${step >= 3 ? 'bg-teal-500' : 'bg-gray-200'}`}></div>
          <div className={`flex items-center justify-center w-8 h-8 rounded-full ${step >= 3 ? 'bg-teal-100 text-teal-800' : 'bg-gray-100 text-gray-500'}`}>
            {step > 3 ? <FiCheckCircle size={16} /> : '3'}
          </div>
          <div className={`flex-1 h-1 mx-2 ${step >= 4 ? 'bg-teal-500' : 'bg-gray-200'}`}></div>
          <div className={`flex items-center justify-center w-8 h-8 rounded-full ${step >= 4 ? 'bg-teal-100 text-teal-800' : 'bg-gray-100 text-gray-500'}`}>
            {step > 4 ? <FiCheckCircle size={16} /> : '4'}
          </div>
        </div>
      </div>
      
      {renderStep()}
      
      <div className="mt-8 flex justify-between">
        <button
          onClick={step === 1 ? handleCancel : handlePrev}
          className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
        >
          <FiChevronLeft size={18} />
          {step === 1 ? 'Cancel' : 'Back'}
        </button>
        <button
          onClick={handleNext}
          disabled={
            (step === 1 && (!plan.name || plan.totalAmount <= 0)) ||
            (step === 4 && !signaturePad?.isEmpty())
          }
          className="flex items-center gap-2 px-4 py-2 bg-teal-600 hover:bg-teal-700 text-white rounded-lg disabled:opacity-50"
        >
          {step === 4 ? 'Complete Plan' : 'Next'}
          <FiChevronRight size={18} />
        </button>
      </div>
    </div>
  );
};

export default PaymentPlanWizard;